import { Settings, Trash2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TestConnectionButton } from './TestConnectionButton';
import type { ChannelConnectionResponseDto } from '@/types/channels';

interface ChannelCardProps {
  channel: ChannelConnectionResponseDto;
  onEdit?: (channelId: string) => void;
  onDelete?: (channelId: string) => void;
}

const STATUS_LABELS: Record<string, string> = {
  active: 'Ativo',
  inactive: 'Inativo',
  configuring: 'Configurando',
  error: 'Erro',
};

const STATUS_VARIANTS: Record<string, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  active: 'default',
  inactive: 'secondary',
  configuring: 'outline',
  error: 'destructive',
};

export function ChannelCard({ channel, onEdit, onDelete }: ChannelCardProps) {
  const status = channel.status ?? '';
  const statusLabel = STATUS_LABELS[status] || status;
  const statusVariant = STATUS_VARIANTS[status] || 'secondary';

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle className="text-lg">{channel.name}</CardTitle>
            <CardDescription className="mt-1">
              {channel.provider} • {channel.channel}
            </CardDescription>
          </div>
          <Badge variant={statusVariant}>{statusLabel}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-sm text-muted-foreground mb-4">
          {channel.lastUsedAt ? (
            <>
              Última utilização:{' '}
              {new Date(channel.lastUsedAt).toLocaleDateString('pt-BR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
              })}
            </>
          ) : (
            'Nunca utilizado'
          )}
        </div>
        <div className="flex gap-2">
          <TestConnectionButton
            channelId={channel.id}
            channelName={channel.name}
            provider={channel.provider}
            variant="outline"
            size="sm"
            className="flex-1"
          />
          <Button
            variant="outline"
            size="sm"
            className="flex-1"
            onClick={() => onEdit?.(channel.id)}
          >
            <Settings className="w-4 h-4 mr-1" />
            Editar
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onDelete?.(channel.id)}
          >
            <Trash2 className="w-4 h-4" />
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
